import React, { useEffect, useState } from "react";

import { Overrides } from "baseui/overrides";
import { FormControlOverrides } from "baseui/form-control";

import FormControl from "../../inputs/form_control";
import { TextareaOverrides } from "../../inputs/textarea";
import Checkbox from "../../inputs/checkbox";
import { InputOverrides } from "../../inputs/input";
import PromptWithSecondaryText from "./shared_components/prompt_with_secondary_text";
import { useConstructAriaLabel } from "./use_construct_aria_label";

import Input from "./input_question_wrapper";
import Textarea from "./textarea_question_wrapper";

export enum MultiSelectFollowUpQuestionType {
  TEXT_INPUT = "text_input",
  TEXT_AREA = "text_area",
}

export enum AnswerCountStrictness {
  MAX_COUNT = "max_count",
  MIN_COUNT = "min_count",
  EXACT_COUNT = "exact_count",
}

export interface MultiSelectWrapperProps {
  questionPrompt: string;
  secondaryText?: string;
  optional: boolean;
  showErrors: boolean;
  options: MultiSelectOption[];
  onChange: (newAnswer: MultiSelectOption) => void;
  setAbleToSubmit: (ableToSubmit: boolean) => void;
  requiredAnswerCount?: number;
  answerCountStrictness?: AnswerCountStrictness;
  dataTest?: string;
}

export interface MultiSelectOption {
  id: string;
  label: string;
  value: boolean;
  followup?: MultiSelectFollowUp;
}

// This interface exists largely because existing question types, used by SIFP questions
// and flags reporting, allowed followup text boxes for some or all checkboxes within a multi-select.
// The shape of this interface has been designed to also fit the 'other' option
// that is present in many questions. Doing so also prevents us from having to
// assume that the final option with oid 'other' always needs to have a text input vs text area.
// It is up to the caller to decide what to do with the followup values.
// This component only knows how to capture the followup data and pass it back up,
// in an effort to keep specific question template type business logic, including
// knowledge of any json structure, out of the presentation layer.
export interface MultiSelectFollowUp {
  type: MultiSelectFollowUpQuestionType;
  optional: boolean;
  value: string | undefined;
  placeholder?: string;
  maxCharacters?: number;
}

export const formControlOverrides: Overrides<FormControlOverrides> = {
  ControlContainer: {
    style: () => ({
      marginTop: "0px",
      marginBottom: "0px",
      width: "fit-content",
    }),
  },
};

const textareaOverrides = (maxCharacters?: number): Overrides<TextareaOverrides> => ({
  Root: {
    style: ({ $theme }) => ({
      marginTop:
        // If we have a character counter, reduce the top margin.
        maxCharacters ? `-${$theme.sizing.scale300}` : `${$theme.sizing.scale800}`,
      maxWidth: "620px",
    }),
  },
});

const inputOverrides = (maxCharacters?: number): Overrides<InputOverrides> => ({
  Root: {
    style: ({ $theme }) => ({
      marginTop:
        // If we have a character counter, reduce the top margin.
        maxCharacters ? `0` : `${$theme.sizing.scale300}`,
      maxWidth: "620px",
    }),
  },
});

const getFollowupErrorMessage = (option: MultiSelectOption) => {
  if (!option.followup) {
    return;
  }

  const requiredAnswerIsMissing = !option.followup.optional && !option.followup.value;
  const answerExceedsCharacterLimit =
    option.followup.maxCharacters &&
    option.followup.value &&
    option.followup.value.length > option.followup.maxCharacters;

  if (requiredAnswerIsMissing) {
    return "Please complete the required field.";
  } else if (option.followup.maxCharacters && answerExceedsCharacterLimit) {
    return `Maximum character limit exceeded by ${
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TS can't see we already guard against undefined in answerExceedsCharacterLimit
      option.followup.value.length - option.followup.maxCharacters
    } characters.`;
  }
};

const MultiSelectQuestionWrapper = ({
  questionPrompt,
  secondaryText,
  optional,
  showErrors,
  options,
  onChange,
  setAbleToSubmit,
  requiredAnswerCount,
  answerCountStrictness,
  dataTest,
}: MultiSelectWrapperProps) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { constructAriaLabel } = useConstructAriaLabel();

  useEffect(() => {
    let ableToSubmit = true;
    const selectedOptions = options.filter((option) => option.value);

    // Due to how many different ways this component can be flexible,
    // and the fact that we are dealing with not one input (or even a radio group)
    // but potentially three different input types, plus an array of checkboxes,
    // the effort to accomplish this with yup was not in scope for TA-752.
    if (!optional) {
      ableToSubmit = selectedOptions.length > 0;

      setErrorMessage(ableToSubmit ? "" : "Please select an option.");

      if (answerCountStrictness === AnswerCountStrictness.EXACT_COUNT && !!requiredAnswerCount) {
        ableToSubmit = selectedOptions.length === requiredAnswerCount;

        setErrorMessage(ableToSubmit ? "" : `Please select ${requiredAnswerCount} options.`);
      } else if (
        answerCountStrictness === AnswerCountStrictness.MAX_COUNT &&
        !!requiredAnswerCount
      ) {
        ableToSubmit = selectedOptions.length > 0 && selectedOptions.length <= requiredAnswerCount;

        setErrorMessage(ableToSubmit ? "" : `Please select up to ${requiredAnswerCount} options.`);
      } else if (
        answerCountStrictness === AnswerCountStrictness.MIN_COUNT &&
        !!requiredAnswerCount
      ) {
        ableToSubmit = selectedOptions.length >= requiredAnswerCount;

        setErrorMessage(
          ableToSubmit ? "" : `Please select at least ${requiredAnswerCount} options.`,
        );
      }
    }

    const selectedOptionsWithFollowupErrors = selectedOptions.filter(
      (option) =>
        option.followup?.type && !option.followup?.optional && getFollowupErrorMessage(option),
    );

    if (selectedOptionsWithFollowupErrors.length > 0) {
      ableToSubmit = false;
    }

    // catch-all
    if (ableToSubmit) {
      setErrorMessage("");
    }

    setAbleToSubmit(ableToSubmit);
    // Adding options and setAbleToSubmit as dependencies causes a "maximum update
    // depth exceeded" warning for follow-up questions
    // eslint-disable-next-line
  }, [answerCountStrictness, optional, requiredAnswerCount]);

  return (
    <>
      <PromptWithSecondaryText
        supplementalQuestionPrompt={questionPrompt}
        secondaryText={secondaryText}
        optional={optional}
        aria-label={`${questionPrompt}${optional ? ` (Optional)` : ""}${
          secondaryText ? ` ${secondaryText}` : ""
        }`}
        dataTest={dataTest}
      />
      {options?.map((option, idx) => {
        const optionIsSelected = option.value;

        // This is a bit of a hack in order to only show the error message once
        // at the very end of the group of checkboxes
        // but not show an error on the checkbox if the error is on the follow-up element
        const formError = idx === options.length - 1 ? errorMessage : "no-caption";

        return (
          <div key={option.id}>
            <FormControl
              key={`form-${option.id}`}
              htmlFor={option.id}
              error={showErrors ? formError : undefined}
              overrides={formControlOverrides}
              dataTest={`${dataTest}-form-control`}
            >
              <Checkbox
                dataTest={`${dataTest}-checkbox-${option.id}`}
                aria-label={
                  idx === 0
                    ? constructAriaLabel(questionPrompt, secondaryText, option.label, optional)
                    : option.label
                }
                key={option.id}
                value={option.id}
                checked={optionIsSelected}
                onChange={(e) => {
                  // For the sake of data cleanliness,
                  // clear out any free responses if unchecking the parent checkbox
                  if (option.followup && !e.currentTarget.checked) {
                    onChange({
                      ...option,
                      value: e.currentTarget.checked,
                      followup: { ...option.followup, value: undefined },
                    });
                  } else {
                    onChange({ ...option, value: e.currentTarget.checked });
                  }
                }}
                error={showErrors && !!errorMessage}
              >
                {option.label}
              </Checkbox>
            </FormControl>
            {optionIsSelected &&
              option.followup?.type === MultiSelectFollowUpQuestionType.TEXT_AREA && (
                <Textarea
                  id={`${option.id}-followup`}
                  key={`${option.id}-followup`}
                  dataTest={`${dataTest}-followup-textarea-${option.id}`}
                  aria-label={constructAriaLabel(
                    undefined,
                    undefined,
                    `Follow up text area for selected option: ${option.label}.`,
                    option.followup?.optional,
                    option.followup?.maxCharacters,
                  )}
                  optional={!!option.followup.optional}
                  placeholder={option.followup.placeholder ?? "Type your response here..."}
                  currentCharacterCount={option.followup.value?.length || 0}
                  maxCharacterCount={option.followup.maxCharacters}
                  errorMessage={showErrors ? getFollowupErrorMessage(option) : undefined}
                  value={option.followup.value}
                  onChange={(e) => {
                    // appease typescript
                    if (option.followup) {
                      onChange({
                        ...option,
                        followup: { ...option.followup, value: e.target.value },
                      });
                    }
                  }}
                  overrides={textareaOverrides(option.followup.maxCharacters)}
                />
              )}
            {optionIsSelected &&
              option.followup?.type === MultiSelectFollowUpQuestionType.TEXT_INPUT && (
                <Input
                  id={`${option.id}-followup`}
                  key={`${option.id}-followup`}
                  dataTest={`${dataTest}-followup-input-${option.id}`}
                  aria-label={constructAriaLabel(
                    undefined,
                    undefined,
                    `Follow up text input for selected option: ${option.label}.`,
                    option.followup?.optional,
                    option.followup?.maxCharacters,
                  )}
                  type="text"
                  optional={!!option.followup.optional}
                  placeholder={option.followup.placeholder ?? "Type your response here..."}
                  currentCharacterCount={option.followup.value?.length || 0}
                  maxCharacterCount={option.followup.maxCharacters}
                  errorMessage={showErrors ? getFollowupErrorMessage(option) : undefined}
                  value={option.followup.value}
                  onChange={(e) => {
                    // appease typescript
                    if (option.followup) {
                      onChange({
                        ...option,
                        followup: { ...option.followup, value: e.target.value },
                      });
                    }
                  }}
                  overrides={inputOverrides(option.followup.maxCharacters)}
                />
              )}
          </div>
        );
      })}
    </>
  );
};

export default MultiSelectQuestionWrapper;
